/**
 * Directory identity and proximity (GH-100, GH-110).
 *
 * Two jobs, both about a donor being certain they are giving to the right organization:
 *
 *  - **EIN** is the identifier that distinguishes two organizations with the same name. It is
 *    handled here as a *string of nine digits*, never a number — leading zeros are significant and
 *    `Number("041234567")` silently loses one.
 *
 *  - **Proximity** ranks directory records against a donor's approximate position. The donor's
 *    precise position never arrives: it is reduced to a coarse grid in the browser before it is
 *    sent (SPEC.md §6.2), so everything here operates on already-coarse input.
 */
/**
 * The two forms an EIN is actually written in: bare digits, or the IRS `NN-NNNNNNN` grouping.
 *
 * Lenient about whether the separator is *there*, strict about where it may be. Stripping every
 * hyphen first would accept `12-34-56789-`, and a validator that accepts malformed input has not
 * validated anything — it has just moved the malformed value one layer deeper.
 */
const EIN_INPUT = /^([0-9]{9}|[0-9]{2}[-\s][0-9]{7})$/;
/**
 * Accepts what a human types — "12-3456789", "12 3456789", "123456789" — and returns the
 * canonical form, or null.
 *
 * Rejecting the hyphenated form would be a validation error that reads to the claimant as "your
 * EIN is wrong", on a form where being turned away means the organization never signs up.
 */
export function parseEin(input) {
    const trimmed = input.trim();
    if (!EIN_INPUT.test(trimmed))
        return null;
    return trimmed.replace(/[-\s]/, "");
}
/**
 * The IRS display form, `12-3456789`.
 *
 * Presentation only. Nothing compares or stores this shape, because "12-3456789" and "123456789"
 * would then be two different organizations.
 */
export function formatEin(ein) {
    return `${ein.slice(0, 2)}-${ein.slice(2)}`;
}
/**
 * How coarse a donor's position becomes before it leaves the browser.
 *
 * Two decimal places is about 1.1 km at the equator and less with latitude. That is far finer than
 * needed to rank the organizations near someone and far coarser than a home address, which is the whole
 * trade: `SPEC.md §6.2` says precise coordinates rank nearby records and are then discarded, and
 * the most reliable way to discard something is never to receive it.
 */
export const POSITION_GRID_DECIMALS = 2;
export function coarsenPosition(position) {
    const factor = 10 ** POSITION_GRID_DECIMALS;
    return {
        latitude: Math.round(position.latitude * factor) / factor,
        longitude: Math.round(position.longitude * factor) / factor,
    };
}
const EARTH_RADIUS_KM = 6371;
const toRadians = (degrees) => (degrees * Math.PI) / 180;
/**
 * Great-circle distance in kilometres.
 *
 * Haversine rather than the equirectangular approximation: the directory is national, and the
 * cheap approximation is wrong by kilometres across the continental United States — enough to
 * reorder two organizations a donor is choosing between.
 */
export function distanceKm(a, b) {
    const dLat = toRadians(b.latitude - a.latitude);
    const dLon = toRadians(b.longitude - a.longitude);
    const lat1 = toRadians(a.latitude);
    const lat2 = toRadians(b.latitude);
    const h = Math.sin(dLat / 2) ** 2 + Math.sin(dLon / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
    return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}
const KM_PER_MILE = 1.609344;
/**
 * Distance as the donor reads it.
 *
 * Deliberately vague below a mile. "0.3 mi" from a coarsened position is a precision that does not
 * exist — the input was rounded to roughly a kilometre — and printing it would claim accuracy the
 * number does not have.
 *
 * `<` rather than the word "under" (owner-directed 2026-08-23). The label shares a directory row
 * with the organization's name, and on a phone that row has width for about half a name — so
 * every character this string does not spend is a character the name keeps. The mathematical
 * symbol says the same thing in a fifth of the space, and it is the one abbreviation on this
 * screen a reader cannot misread as something else.
 */
export function formatDistance(km) {
    const miles = km / KM_PER_MILE;
    if (miles < 1)
        return "< 1 mi";
    if (miles < 10)
        return `${miles.toFixed(1)} mi`;
    return `${Math.round(miles)} mi`;
}
/**
 * How many results the donor sees before asking for more.
 *
 * Three is the request and also the right number: it fits above the fold on a phone with the
 * search field still visible, so "none of these" costs one glance rather than a scroll.
 */
export const NEAREST_RESULT_COUNT = 3;
/** The ceiling on any single directory response, whatever a client asks for. */
export const MAX_DIRECTORY_RESULTS = 50;
/**
 * "Near" as the find-events screen means it: about a mile.
 *
 * 1.7 km rather than 1.609 km because the donor's own position was coarsened to a ~1 km grid
 * before it ever reached the server (SPEC.md §6.2) — an organization exactly a mile away can measure up
 * to ~half a kilometre further from the grid point than from the donor, and a threshold that
 * ignored that would hide "the organization down the road" on the wrong side of a rounding.
 */
export const NEARBY_RADIUS_KM = 1.7;
/**
 * "Near me" as the app's home screen means it: five miles.
 *
 * Home shows events a donor could actually attend tonight, which is a different question from
 * "which organization is closest" — hence a different radius from `NEARBY_RADIUS_KM`. Five miles is the
 * owner's number (2026-08-22); the extra 0.45 km is the same coarsening slack `NEARBY_RADIUS_KM`
 * carries, because the donor's position was rounded to a ~1 km grid before it was sent and a
 * threshold that ignored that would hide an event on the wrong side of a rounding.
 */
export const NEARBY_EVENT_RADIUS_KM = 5 * KM_PER_MILE + 0.45;
/** The furthest any client may ask the directory to look, whatever radius it passes. */
export const MAX_NEARBY_RADIUS_KM = 200;
//# sourceMappingURL=directory.js.map